import {
  Combobox,
  type ComboboxOption
} from '@library/components/selects/combobox/Combobox';
import { Input, TextArea } from '@library/components/inputs';
import { useMemo, useState } from 'react';
import type { CreateRolesRequest } from '@library/types/role';
import { FormActionButtons } from '@library/components/buttons';
import { Modal } from '@library/components/modals/Modal';
import type { TransactionMaxValues } from '@library/types/permissions';
import { UserRoleTransactionMax } from '@bo-components/userRoles/UserRoleTransactionMax';
import { getTransactionMaxByPermissionValues } from '@bo-utils/user';
import { isValidArray } from '@bo-utils/common';
import { usePermissions } from '@bo-hooks/permissions/usePermissions';

export const CreateRoleModal = ({
  isOpen,
  onCancelModal,
  onSave
}: {
  isOpen: boolean;
  onCancelModal: () => void;
  onSave: (role: CreateRolesRequest) => void;
}) => {
  const { permissionComboboxOptions } = usePermissions();
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [permissions, setPermissions] = useState<Array<ComboboxOption> | null>(
    []
  );
  const [transactionMaxes, setTransactionMaxes] = useState<
    Array<TransactionMaxValues>
  >([]);

  const handlePermissionsChange = (newValues: Array<ComboboxOption> | null) => {
    setPermissions(newValues);

    const newTransactionMaxes = getTransactionMaxByPermissionValues(newValues);
    setTransactionMaxes(newTransactionMaxes);
  };

  const saveDisabled = useMemo(
    () => isSaveDisabled || !roleName || !isValidArray(permissions),
    [isSaveDisabled, permissions, roleName]
  );

  const resetFields = () => {
    setRoleName('');
    setRoleDescription('');
    setPermissions([]);
    setTransactionMaxes([]);
  };

  const handleCancelModal = () => {
    resetFields();
    onCancelModal();
  };

  const handleSubmit = () => {
    setIsSaveDisabled(true);

    onSave({
      name: roleName,
      description: roleDescription
      // TODO: Add permissionIds and transactionMaxes once roles endpoint is updated
      // permissionIds:
      //   permissions?.map((permission) => String(permission.value)) ?? []
      // transactionMaxes
    });
  };

  const handleTransactionMaxChange = (values: Array<TransactionMaxValues>) => {
    setTransactionMaxes(values);
  };

  return (
    <Modal isOpen={isOpen} onCancelModal={handleCancelModal}>
      <div className="bg-soFresh-100 border-soFresh-140 relative mx-auto w-[45rem] flex-col rounded-md border">
        <div className="text-soClean-100 border-soFresh-140 flex h-10 items-center justify-between rounded-t border-b border-solid px-4 font-medium">
          <span>Create New Role</span>
        </div>
        <div className="flex max-h-[70vh] flex-col gap-y-2 overflow-y-scroll p-4">
          <Input
            labelName="Role Name"
            value={roleName}
            onValueChange={setRoleName}
          />
          <TextArea
            className="h-20 min-h-[80px] resize-none"
            labelName="Description"
            value={roleDescription}
            onValueChange={setRoleDescription}
          />
          <Combobox
            id="permissions"
            isMulti
            label="Permissions"
            value={permissions}
            placeholder="Select permissions..."
            onChange={handlePermissionsChange}
            options={permissionComboboxOptions}
            allowSelectAll
            isClearable
          />
          {!!transactionMaxes.length && (
            <UserRoleTransactionMax
              transactionMaxes={transactionMaxes}
              onTransactionMaxChange={handleTransactionMaxChange}
            />
          )}
        </div>
        <FormActionButtons
          primary={{ onClick: handleSubmit, disabled: saveDisabled }}
          secondary={{ onClick: handleCancelModal }}
        />
      </div>
    </Modal>
  );
};
